import { useState, useEffect, useCallback } from "react";
import Swal from "sweetalert2";
import { supabase } from "@/lib/supabaseClient";
import type { Job } from "./JobModal";
import { STORAGE_CONFIG, isVpsStorageReady } from "@/lib/storageConfig";
import UploadField from "@/components/base/UploadField";
import ModuleGate from "@/components/base/ModuleGate";
import { deleteFileFromVps } from "@/lib/vpsStorage";

const _vpsCheck = isVpsStorageReady;

export type JobDelivery = {
  id: string;
  tenant_id: string;
  job_id: string;
  client_id: string;
  delivery_type: string;
  title: string;
  description: string | null;
  file_url: string;
  thumbnail_url: string | null;
  expires_at: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
};

const DELIVERY_TYPES = [
  { value: "gallery", label: "Online Gallery", icon: "ri-gallery-line" },
  { value: "video", label: "Video", icon: "ri-video-line" },
  { value: "zip", label: "ZIP Download", icon: "ri-file-zip-line" },
  { value: "photos", label: "Photos", icon: "ri-image-2-line" },
  { value: "documents", label: "Documents", icon: "ri-file-text-line" },
  { value: "other", label: "Other", icon: "ri-link-m" },
];

const TYPE_STYLES: Record<string, string> = {
  gallery: "bg-violet-50 text-violet-600",
  video: "bg-sky-50 text-sky-600",
  zip: "bg-amber-50 text-amber-600",
  photos: "bg-emerald-50 text-emerald-600",
  documents: "bg-orange-50 text-orange-600",
  other: "bg-slate-100 text-slate-500",
};

const TYPE_ICONS: Record<string, string> = {
  gallery: "ri-gallery-line",
  video: "ri-video-line",
  zip: "ri-file-zip-line",
  photos: "ri-image-2-line",
  documents: "ri-file-text-line",
  other: "ri-link-m",
};

const EMPTY_FORM = {
  delivery_type: "gallery",
  title: "",
  description: "",
  file_url: "",
  thumbnail_url: "",
  expires_at: "",
};

type FormState = typeof EMPTY_FORM;

function DeliveryForm({
  initial,
  onSave,
  onCancel,
  saving,
  tenantId,
  entityType,
  entityId,
}: {
  initial: FormState;
  onSave: (form: FormState, fileId?: string, thumbFileId?: string) => void;
  onCancel: () => void;
  saving: boolean;
  tenantId: string;
  entityType: string;
  entityId: string;
}) {
  const [form, setForm] = useState<FormState>(initial);
  const [fileId, setFileId] = useState<string | undefined>(undefined);
  const [thumbFileId, setThumbFileId] = useState<string | undefined>(undefined);
  const set = (key: keyof FormState, val: string) =>
    setForm((prev) => ({ ...prev, [key]: val }));

  const isValid = form.title.trim() && form.file_url.trim();

  return (
    <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 uppercase tracking-wider">
            Type
          </label>
          <select
            value={form.delivery_type}
            onChange={(e) => set("delivery_type", e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#2563eb]/50 transition-colors appearance-none cursor-pointer"
          >
            {DELIVERY_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 uppercase tracking-wider">
            Title <span className="text-red-400">*</span>
          </label>
          <input
            type="text"
            value={form.title}
            onChange={(e) => set("title", e.target.value)}
            placeholder="Wedding Gallery 2024"
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#2563eb]/50 transition-colors"
          />
        </div>
      </div>

      <UploadField
        label="File / Link URL"
        value={form.file_url}
        onChange={(url) => set("file_url", url)}
        accept="*/*"
        hint="Gallery, ZIP, video, or any file — up to 2 GB"
        urlPlaceholder="https://drive.google.com/... or https://gallery.example.com/..."
        required
        tenantId={tenantId}
        entityType="job_delivery"
        entityId={entityId}
        folder="deliveries"
        maxSizeMB={2000}
        onUploaded={(_url, meta) => setFileId(meta.fileId)}
      />

      <div className="grid grid-cols-2 gap-3">
        <UploadField
          label="Thumbnail"
          value={form.thumbnail_url}
          onChange={(url) => set("thumbnail_url", url)}
          accept="image/*"
          hint="Optional preview image"
          urlPlaceholder="https://..."
          showPreview
          tenantId={tenantId}
          entityType="job_delivery"
          entityId={entityId}
          folder="deliveries"
          maxSizeMB={20}
          allowedTypes={["image/jpeg", "image/png", "image/webp"]}
          onUploaded={(_url, meta) => setThumbFileId(meta.fileId)}
        />
        <div>
          <label className="block text-slate-400 text-xs mb-1.5 uppercase tracking-wider">
            Expires At <span className="text-slate-300">(optional)</span>
          </label>
          <input
            type="datetime-local"
            value={form.expires_at}
            onChange={(e) => set("expires_at", e.target.value)}
            className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] outline-none focus:border-[#2563eb]/50 transition-colors"
          />
        </div>
      </div>

      <div>
        <label className="block text-slate-400 text-xs mb-1.5 uppercase tracking-wider">
          Description <span className="text-slate-300">(optional)</span>
        </label>
        <textarea
          value={form.description}
          onChange={(e) => set("description", e.target.value)}
          placeholder="Instructions or notes for the client…"
          rows={2}
          className="w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm text-[#0f172a] placeholder-slate-300 outline-none focus:border-[#2563eb]/50 transition-colors resize-none"
        />
      </div>

      <div className="flex gap-2 justify-end pt-1">
        <button
          onClick={onCancel}
          className="px-4 py-2 text-sm text-slate-400 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          Cancel
        </button>
        <button
          onClick={() => isValid && onSave(form, fileId, thumbFileId)}
          disabled={!isValid || saving}
          className="flex items-center gap-2 bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed text-black text-sm font-semibold px-4 py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          {saving ? (
            <div className="w-4 h-4 border-2 border-black border-t-transparent rounded-full animate-spin" />
          ) : (
            <>
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-save-line"></i>
              </span>
              Save Delivery
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function DeliveryCard({
  delivery,
  onEdit,
  onToggleActive,
  onDelete,
  onCopyLink,
  copiedId,
  busy,
}: {
  delivery: JobDelivery;
  onEdit: (d: JobDelivery) => void;
  onToggleActive: (d: JobDelivery) => void;
  onDelete: (d: JobDelivery) => void;
  onCopyLink: (d: JobDelivery) => void;
  copiedId: string | null;
  busy: boolean;
}) {
  const isExpired =
    !!delivery.expires_at && new Date(delivery.expires_at) < new Date();
  const typeLabel =
    DELIVERY_TYPES.find((t) => t.value === delivery.delivery_type)?.label ??
    delivery.delivery_type;

  return (
    <div
      className={`rounded-xl border p-4 transition-all ${
        !delivery.is_active
          ? "border-slate-100 bg-white opacity-50"
          : isExpired
            ? "border-red-200 bg-red-50/30"
            : "border-slate-200 bg-white"
      }`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-9 h-9 flex items-center justify-center rounded-lg shrink-0 ${
            TYPE_STYLES[delivery.delivery_type] ?? "bg-slate-100 text-slate-500"
          }`}
        >
          <i
            className={`${TYPE_ICONS[delivery.delivery_type] ?? "ri-link-m"} text-base`}
          ></i>
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-[#0f172a] text-sm font-medium truncate">
                {delivery.title}
              </p>
              <span
                className={`text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${
                  TYPE_STYLES[delivery.delivery_type] ??
                  "bg-slate-100 text-slate-500"
                }`}
              >
                {typeLabel}
              </span>
              {!delivery.is_active && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-400 whitespace-nowrap">
                  Inactive
                </span>
              )}
              {isExpired && delivery.is_active && (
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-red-50 text-red-500 whitespace-nowrap">
                  Expired
                </span>
              )}
            </div>

            <div className="flex items-center gap-1 shrink-0">
              {busy ? (
                <div className="w-4 h-4 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <a
                    href={delivery.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#2563eb] hover:bg-blue-50 rounded-lg transition-all cursor-pointer"
                    title="Open link"
                  >
                    <i className="ri-external-link-line text-sm"></i>
                  </a>
                  <button
                    onClick={() => onCopyLink(delivery)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                      copiedId === delivery.id
                        ? "text-emerald-500 bg-emerald-50"
                        : "text-slate-400 hover:text-[#0f172a] hover:bg-slate-100"
                    }`}
                    title="Copy link"
                  >
                    <i className={`text-sm ${copiedId === delivery.id ? "ri-check-line" : "ri-link"}`}></i>
                  </button>
                  <button
                    onClick={() => onEdit(delivery)}
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer"
                    title="Edit"
                  >
                    <i className="ri-pencil-line text-sm"></i>
                  </button>
                  <button
                    onClick={() => onToggleActive(delivery)}
                    className={`w-7 h-7 flex items-center justify-center rounded-lg transition-all cursor-pointer ${
                      delivery.is_active
                        ? "text-slate-400 hover:text-amber-500 hover:bg-amber-50"
                        : "text-emerald-500/50 hover:text-emerald-600 hover:bg-emerald-50"
                    }`}
                    title={delivery.is_active ? "Deactivate" : "Activate"}
                  >
                    <i
                      className={`text-sm ${
                        delivery.is_active ? "ri-eye-off-line" : "ri-eye-line"
                      }`}
                    ></i>
                  </button>
                  <button
                    onClick={() => onDelete(delivery)}
                    className="w-7 h-7 flex items-center justify-center text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-all cursor-pointer"
                    title="Delete"
                  >
                    <i className="ri-delete-bin-line text-sm"></i>
                  </button>
                </>
              )}
            </div>
          </div>

          {delivery.description && (
            <p className="text-slate-500 text-xs mb-2 line-clamp-2">
              {delivery.description}
            </p>
          )}

          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-slate-300 text-xs truncate max-w-[260px] font-mono">
              {delivery.file_url}
            </p>
            {delivery.expires_at && (
              <span
                className={`text-xs whitespace-nowrap ${
                  isExpired ? "text-red-400" : "text-slate-400"
                }`}
              >
                {isExpired ? "Expired" : "Expires"}{" "}
                {new Date(delivery.expires_at).toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                })}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

interface Props {
  job: Job;
  tenantId: string;
  clientName: string;
  onClose: () => void;
}

export default function DeliveriesModal({
  job,
  tenantId,
  clientName,
  onClose,
}: Props) {
  const [deliveries, setDeliveries] = useState<JobDelivery[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingDelivery, setEditingDelivery] = useState<JobDelivery | null>(
    null,
  );
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);

  const fetchDeliveries = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("job_deliveries")
      .select("*")
      .eq("job_id", job.id)
      .eq("tenant_id", tenantId)
      .order("created_at", { ascending: false });

    if (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        background: "#fff",
        color: "#1e293b",
      });
    } else {
      setDeliveries((data as JobDelivery[]) ?? []);
    }
    setLoading(false);
  }, [job.id, tenantId]);

  useEffect(() => {
    fetchDeliveries();
  }, [fetchDeliveries]);

  const handleSave = async (form: typeof EMPTY_FORM, fileId?: string, thumbFileId?: string) => {
    setSaving(true);
    if (editingDelivery) {
      const { data, error } = await supabase
        .from("job_deliveries")
        .update({
          delivery_type: form.delivery_type,
          title: form.title,
          description: form.description || null,
          file_url: form.file_url,
          thumbnail_url: form.thumbnail_url || null,
          expires_at: form.expires_at || null,
          updated_at: new Date().toISOString(),
        })
        .eq("id", editingDelivery.id)
        .eq("tenant_id", tenantId)
        .select()
        .maybeSingle();

      if (error) {
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          background: "#fff",
          color: "#1e293b",
        });
      } else if (data) {
        setDeliveries((prev) =>
          prev.map((d) =>
            d.id === editingDelivery.id ? (data as JobDelivery) : d,
          ),
        );
        Swal.fire({
          title: "Updated",
          text: "Delivery updated successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          background: "#fff",
          color: "#1e293b",
        });
      }
      setEditingDelivery(null);
    } else {
      const { data, error } = await supabase
        .from("job_deliveries")
        .insert({
          tenant_id: tenantId,
          job_id: job.id,
          client_id: job.client_id,
          delivery_type: form.delivery_type,
          title: form.title,
          description: form.description || null,
          file_url: form.file_url,
          thumbnail_url: form.thumbnail_url || null,
          expires_at: form.expires_at || null,
          is_active: true,
        })
        .select()
        .maybeSingle();

      if (error) {
        // Cleanup uploaded files if DB insert failed
        if (fileId) {
          try {
            await deleteFileFromVps({ fileId, tenantId, silent: true });
          } catch {
            // ignore
          }
        }
        if (thumbFileId) {
          try {
            await deleteFileFromVps({ fileId: thumbFileId, tenantId, silent: true });
          } catch {
            // ignore
          }
        }
        Swal.fire({
          title: "Error",
          text: error.message,
          icon: "error",
          background: "#fff",
          color: "#1e293b",
        });
      } else if (data) {
        setDeliveries((prev) => [data as JobDelivery, ...prev]);
        Swal.fire({
          title: "Delivery Added",
          text: "New delivery created successfully.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
          background: "#fff",
          color: "#1e293b",
        });
      }
    }
    setShowForm(false);
    setSaving(false);
  };

  const handleToggleActive = async (delivery: JobDelivery) => {
    setBusyId(delivery.id);
    const { data, error } = await supabase
      .from("job_deliveries")
      .update({
        is_active: !delivery.is_active,
        updated_at: new Date().toISOString(),
      })
      .eq("id", delivery.id)
      .eq("tenant_id", tenantId)
      .select()
      .maybeSingle();

    if (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        background: "#fff",
        color: "#1e293b",
      });
    } else if (data) {
      setDeliveries((prev) =>
        prev.map((d) => (d.id === delivery.id ? (data as JobDelivery) : d)),
      );
    }
    setBusyId(null);
  };

  const handleDelete = async (delivery: JobDelivery) => {
    const result = await Swal.fire({
      title: "Delete Delivery?",
      text: `"${delivery.title}" will be permanently removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
      cancelButtonText: "Cancel",
      background: "#fff",
      color: "#1e293b",
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#94a3b8",
    });
    if (!result.isConfirmed) return;

    setBusyId(delivery.id);

    // Delete from VPS if file exists
    const { data: fileRecord } = await supabase
      .from("uploaded_files")
      .select("id")
      .eq("file_url", delivery.file_url)
      .eq("tenant_id", tenantId)
      .maybeSingle();
    if (fileRecord?.id) {
      try {
        await deleteFileFromVps({ fileId: fileRecord.id, tenantId, silent: true });
      } catch {
        // Continue even if VPS delete fails
      }
    }
    // Also delete thumbnail if exists
    if (delivery.thumbnail_url) {
      const { data: thumbRecord } = await supabase
        .from("uploaded_files")
        .select("id")
        .eq("file_url", delivery.thumbnail_url)
        .eq("tenant_id", tenantId)
        .maybeSingle();
      if (thumbRecord?.id) {
        try {
          await deleteFileFromVps({ fileId: thumbRecord.id, tenantId, silent: true });
        } catch {
          // Continue
        }
      }
    }

    const { error } = await supabase
      .from("job_deliveries")
      .delete()
      .eq("id", delivery.id)
      .eq("tenant_id", tenantId);

    if (error) {
      Swal.fire({
        title: "Error",
        text: error.message,
        icon: "error",
        background: "#fff",
        color: "#1e293b",
      });
    } else {
      setDeliveries((prev) => prev.filter((d) => d.id !== delivery.id));
    }
    setBusyId(null);
  };

  const openEdit = (delivery: JobDelivery) => {
    setEditingDelivery(delivery);
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingDelivery(null);
  };

  const [copiedId, setCopiedId] = useState<string | null>(null);

  const handleCopyLink = async (d: JobDelivery) => {
    try {
      await navigator.clipboard.writeText(d.file_url);
      setCopiedId(d.id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = d.file_url;
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand("copy");
        setCopiedId(d.id);
        setTimeout(() => setCopiedId(null), 2000);
      } catch {
        Swal.fire({
          title: "Could not copy",
          text: "Your browser does not support clipboard copy.",
          icon: "error",
          background: "#fff",
          color: "#1e293b",
          timer: 2000,
          showConfirmButton: false,
        });
      }
      document.body.removeChild(ta);
    }
  };

  const editInitial: typeof EMPTY_FORM = editingDelivery
    ? {
        delivery_type: editingDelivery.delivery_type,
        title: editingDelivery.title,
        description: editingDelivery.description ?? "",
        file_url: editingDelivery.file_url,
        thumbnail_url: editingDelivery.thumbnail_url ?? "",
        expires_at: editingDelivery.expires_at
          ? editingDelivery.expires_at.slice(0, 16)
          : "",
      }
    : EMPTY_FORM;

  const activeCount = deliveries.filter((d) => d.is_active).length;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-2xl border border-slate-200 w-full max-w-3xl mx-4 overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 shrink-0">
          <div className="min-w-0 flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1">
              <span className="w-4 h-4 flex items-center justify-center text-[#2563eb]">
                <i className="ri-send-plane-line text-sm"></i>
              </span>
              <p className="text-slate-400 text-xs uppercase tracking-wider">
                Final Deliveries
              </p>
            </div>
            <h2 className="text-[#0f172a] font-semibold text-base truncate">
              {job.title}
            </h2>
            <p className="text-slate-400 text-xs mt-0.5">{clientName}</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center text-slate-400 hover:text-[#0f172a] hover:bg-slate-100 rounded-lg transition-all cursor-pointer shrink-0"
          >
            <i className="ri-close-line"></i>
          </button>
        </div>

        {/* Stats bar */}
        <div className="px-6 py-3 border-b border-slate-200 shrink-0 flex items-center justify-between">
          <div className="flex items-center gap-4 text-xs text-slate-400">
            <span>{deliveries.length} deliveries</span>
            <span className="text-emerald-600">{activeCount} active</span>
            <span className="text-slate-300">
              {deliveries.length - activeCount} inactive
            </span>
          </div>
          {!showForm && (
            <button
              onClick={() => {
                setEditingDelivery(null);
                setShowForm(true);
              }}
              className="flex items-center gap-1.5 bg-[#2563eb] hover:bg-blue-700 text-black text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
            >
              <span className="w-3.5 h-3.5 flex items-center justify-center">
                <i className="ri-add-line"></i>
              </span>
              Add Delivery
            </button>
          )}
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1">
          <ModuleGate moduleId="final_deliveries" moduleName="Final Deliveries">
            <div className="px-6 py-5 space-y-4">
              {showForm && (
                <DeliveryForm
                  initial={editInitial}
                  onSave={handleSave}
                  onCancel={cancelForm}
                  saving={saving}
                  tenantId={tenantId}
                  entityType="job_delivery"
                  entityId={job.id}
                />
              )}

              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-[#C8A96E] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : deliveries.length === 0 ? (
                <div className="flex flex-col items-center gap-3 py-12">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-slate-50 text-slate-300">
                    <i className="ri-send-plane-line text-2xl"></i>
                  </div>
                  <p className="text-slate-400 text-sm">
                    No deliveries yet — add the first one above
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  {deliveries.map((d) => (
                    <DeliveryCard
                      key={d.id}
                      delivery={d}
                      onEdit={openEdit}
                      onToggleActive={handleToggleActive}
                      onDelete={handleDelete}
                      onCopyLink={handleCopyLink}
                      copiedId={copiedId}
                      busy={busyId === d.id}
                    />
                  ))}
                </div>
              )}
            </div>
          </ModuleGate>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-slate-200 shrink-0 flex justify-end">
          <button
            onClick={onClose}
            className="px-5 py-2 text-sm text-slate-500 hover:text-[#0f172a] border border-slate-200 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}